import React, { useState, useEffect, useCallback } from "react";
import { Users, Shield, TrendingUp, Edit2, Trash2, Search, Filter, Download, CheckSquare, Square } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function AdminMembers() {
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ tier: "", status: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMembers, setSelectedMembers] = useState(new Set());
  const [bulkAction, setBulkAction] = useState("");

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get("/api/admin/members/stats");
      setStats(res.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.tier) params.tier = filter.tier;
      if (filter.status) params.status = filter.status;
      
      const res = await api.get("/api/admin/members/list", { params });
      setMembers(res.data.members || []);
    } catch (error) {
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchStats();
    fetchMembers();
  }, [fetchStats, fetchMembers]);

  const updateTier = async (memberId, newTier) => {
    try {
      await api.patch(`/api/admin/members/${memberId}/tier`, { tier: newTier });
      toast.success(`Tier updated to ${newTier}`);
      fetchMembers();
      fetchStats();
    } catch (error) {
      toast.error("Failed to update tier");
    }
  };

  const updateStatus = async (memberId, newStatus) => {
    try {
      await api.patch(`/api/admin/members/${memberId}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchMembers();
      fetchStats();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteMember = async (memberId, email) => {
    if (!window.confirm(`Delete member ${email}? This action cannot be undone.`)) return;
    
    try {
      await api.delete(`/api/admin/members/${memberId}`);
      toast.success("Member deleted");
      fetchMembers();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete member");
    }
  };

  const toggleSelectMember = (memberId) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelectedMembers(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedMembers.size === filteredMembers.length && filteredMembers.length > 0) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(filteredMembers.map(m => m.member_id)));
    }
  };

  const handleBulkAction = async () => {
    if (selectedMembers.size === 0) {
      toast.error("No members selected");
      return;
    }

    if (!bulkAction) {
      toast.error("Please select a bulk action");
      return;
    }

    const memberIds = Array.from(selectedMembers);

    try {
      if (bulkAction === "delete") {
        if (!window.confirm(`Delete ${memberIds.length} member(s)? This action cannot be undone.`)) return;
        
        const res = await api.post("/api/admin/members/bulk/delete", { member_ids: memberIds });
        toast.success(res.data.message || `Deleted ${res.data.deleted_count} member(s)`);
      } else if (bulkAction.startsWith("tier:")) {
        const tier = bulkAction.split(":")[1];
        const res = await api.post("/api/admin/members/bulk/tier", { member_ids: memberIds, tier });
        toast.success(res.data.message || `Updated ${res.data.modified_count} member(s)`);
      } else if (bulkAction.startsWith("status:")) {
        const status = bulkAction.split(":")[1];
        const res = await api.post("/api/admin/members/bulk/status", { member_ids: memberIds, status });
        toast.success(res.data.message || `Updated ${res.data.modified_count} member(s)`);
      }

      // Refresh data
      setSelectedMembers(new Set());
      setBulkAction("");
      fetchMembers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk action failed");
    }
  };

  const exportToCSV = async () => {
    try {
      const params = {};
      if (filter.tier) params.tier = filter.tier;
      if (filter.status) params.status = filter.status;

      const response = await api.get("/api/admin/members/export/csv", {
        params,
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `members_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Members exported to CSV");
    } catch (error) {
      toast.error("Failed to export members");
    }
  };

  const filteredMembers = members.filter(m =>
    m.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (m.name && m.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const tierColors = {
    free: "bg-gray-500/20 text-gray-300 border-gray-500/40",
    basic: "bg-green-500/20 text-green-300 border-green-500/40",
    members: "bg-green-500/20 text-green-300 border-green-500/40",
    vip: "bg-purple-500/20 text-purple-300 border-purple-500/40"
  };

  const statusColors = {
    active: "bg-green-500/20 text-green-300",
    suspended: "bg-red-500/20 text-red-300",
    cancelled: "bg-gray-500/20 text-gray-300"
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Members Management</h1>
          <p className="text-zinc-400">Manage free and paid member accounts</p>
        </div>
        <Users size={40} className="text-purple-400" />
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users size={20} className="text-blue-400" />
              <span className="text-sm text-zinc-400">Total Members</span>
            </div>
            <div className="text-3xl font-bold text-white">{stats.total}</div>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp size={20} className="text-gray-400" />
              <span className="text-sm text-zinc-400">Free</span>
            </div>
            <div className="text-3xl font-bold text-white">{stats.by_tier.free}</div>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Shield size={20} className="text-green-400" />
              <span className="text-sm text-zinc-400">Basic</span>
            </div>
            <div className="text-3xl font-bold text-white">{stats.by_tier.basic}</div>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Shield size={20} className="text-purple-400" />
              <span className="text-sm text-zinc-400">VIP</span>
            </div>
            <div className="text-3xl font-bold text-white">{stats.by_tier.vip}</div>
          </div>
        </div>
      )}

      {/* Filters & Search */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">
              <Search size={14} className="inline mr-1" />
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Email or name..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm text-zinc-400 mb-2">
              <Filter size={14} className="inline mr-1" />
              Tier
            </label>
            <select
              value={filter.tier}
              onChange={(e) => setFilter({ ...filter, tier: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
            >
              <option value="">All Tiers</option>
              <option value="free">Free</option>
              <option value="basic">Basic</option>
              <option value="vip">VIP</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-zinc-400 mb-2">
              <Filter size={14} className="inline mr-1" />
              Status
            </label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions & Export */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-4 border-t border-zinc-800">
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <CheckSquare size={16} />
            <span>{selectedMembers.size} selected</span>
          </div>
          
          {selectedMembers.size > 0 && (
            <>
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white text-sm"
              >
                <option value="">Select bulk action...</option>
                <optgroup label="Change Tier">
                  <option value="tier:free">Set to Free</option>
                  <option value="tier:basic">Set to Basic</option>
                  <option value="tier:vip">Set to VIP</option>
                </optgroup>
                <optgroup label="Change Status">
                  <option value="status:active">Set to Active</option>
                  <option value="status:suspended">Set to Suspended</option>
                  <option value="status:cancelled">Set to Cancelled</option>
                </optgroup>
                <optgroup label="Delete">
                  <option value="delete">Delete Selected</option>
                </optgroup>
              </select>
              
              <button
                onClick={handleBulkAction}
                disabled={!bulkAction}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition"
              >
                Apply
              </button>
            </>
          )}

          <button
            onClick={exportToCSV}
            className="ml-auto px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg text-sm font-medium transition flex items-center gap-2"
          >
            <Download size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-800">
              <tr>
                <th className="px-6 py-4 text-left">
                  <button
                    onClick={toggleSelectAll}
                    className="text-zinc-400 hover:text-white transition"
                  >
                    {selectedMembers.size === filteredMembers.length && filteredMembers.length > 0 ? (
                      <CheckSquare size={18} />
                    ) : (
                      <Square size={18} />
                    )}
                  </button>
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Member</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Tier</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Created</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-zinc-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-zinc-400">
                    Loading members...
                  </td>
                </tr>
              ) : filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-zinc-400">
                    No members found
                  </td>
                </tr>
              ) : (
                filteredMembers.map((member) => (
                  <tr key={member.member_id} className="hover:bg-zinc-800/50 transition">
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleSelectMember(member.member_id)}
                        className="text-zinc-400 hover:text-white transition"
                      >
                        {selectedMembers.has(member.member_id) ? (
                          <CheckSquare size={18} className="text-purple-400" />
                        ) : (
                          <Square size={18} />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-white">{member.name || "N/A"}</div>
                      <div className="text-xs text-zinc-400">{member.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={member.tier}
                        onChange={(e) => updateTier(member.member_id, e.target.value)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold border ${tierColors[member.tier]} bg-transparent`}
                      >
                        <option value="free">Free</option>
                        <option value="basic">Basic</option>
                        <option value="vip">VIP</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={member.status}
                        onChange={(e) => updateStatus(member.member_id, e.target.value)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[member.status]} bg-transparent border-0`}
                      >
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-sm text-zinc-400">
                      {new Date(member.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => deleteMember(member.member_id, member.email)}
                        className="text-red-400 hover:text-red-300 transition"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
